// src/lib/headerSlot.ts
//
// The page's title, eyebrow, controls and kebab menu live in the shell's top bar (as on the
// samksly admin), not in the page body. A page declares them with <PageHeader>; the shell
// reads them here. A tiny external store rather than context: the page writes on every
// render and only the top bar re-renders, so there is no provider → page → provider loop.
import { useSyncExternalStore, type ReactNode } from 'react';
import type { MenuEntry } from '../components/ui/menu';

export interface HeaderSlot {
  title: ReactNode;
  eyebrow?: ReactNode;
  /** Where the back chevron goes (detail pages). */
  back?: string;
  /** Controls that are not buttons: a period picker, a year select, status badges. */
  actions?: ReactNode;
  /** Every button the page offers, collapsed into one ⋯ menu. */
  menu?: MenuEntry[];
}

let slot: HeaderSlot | null = null;
let owner: symbol | null = null;
const listeners = new Set<() => void>();

export function setHeaderSlot(next: HeaderSlot, by: symbol) {
  slot = next; owner = by;
  listeners.forEach((l) => l());
}
/** Only the page that wrote the slot may clear it (a leaving page must not wipe the arriving one's title). */
export function clearHeaderSlot(by: symbol) {
  if (owner !== by) return;
  slot = null; owner = null;
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const snapshot = () => slot;

export function useHeaderSlot(): HeaderSlot | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
